package com.merkost.handpass.admin.container

import com.merkost.handpass.admin.api.AdminRepositoryApi
import com.merkost.handpass.admin.contract.AsyncSection
import com.merkost.handpass.admin.contract.UsersAction
import com.merkost.handpass.admin.contract.UsersIntent
import com.merkost.handpass.admin.contract.UsersState
import com.merkost.handpass.admin.platform.WindowFocusTrigger
import com.merkost.handpass.admin.util.toAdminMessage
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.launch
import pro.respawn.flowmvi.api.Container
import pro.respawn.flowmvi.api.PipelineContext
import pro.respawn.flowmvi.dsl.lazyStore
import pro.respawn.flowmvi.plugins.recover
import pro.respawn.flowmvi.plugins.reduce

@OptIn(FlowPreview::class)
class UsersContainer(
    private val repo: AdminRepositoryApi,
    private val focusTrigger: WindowFocusTrigger,
) : Container<UsersState, UsersIntent, UsersAction> {

    private val scope = MainScope()

    private val searchFlow = MutableSharedFlow<String>(replay = 1)

    override val store by lazyStore(UsersState(), scope) {
        configure { name = "UsersStore" }
        recover { e ->
            updateState { copy(data = AsyncSection.Failed(e.toAdminMessage())) }
            null
        }
        reduce { intent ->
            when (intent) {
                is UsersIntent.SetStatusFilter -> {
                    updateState { copy(statusFilter = intent.value, page = 1) }
                    reload()
                }
                is UsersIntent.SetSearch -> {
                    updateState { copy(search = intent.value) }
                    searchFlow.tryEmit(intent.value)
                }
                is UsersIntent.GoToPage -> {
                    updateState { copy(page = intent.page) }
                    reload()
                }
                is UsersIntent.TapUser -> {
                    action(UsersAction.Navigate("/users/${intent.id}"))
                }
                is UsersIntent.ReactToFocusTick, UsersIntent.Refresh -> reload()
                is UsersIntent.SetSort -> {
                    updateState { copy(sortBy = intent.sortBy, sortAsc = intent.sortAsc, page = 1) }
                    reload()
                }
            }
        }
    }

    init {
        scope.launch {
            focusTrigger.ticks.collect { tick -> store.intent(UsersIntent.ReactToFocusTick(tick)) }
        }
        scope.launch {
            searchFlow.debounce(300).distinctUntilChanged().collect { store.intent(UsersIntent.GoToPage(1)) }
        }
    }

    private suspend fun PipelineContext<UsersState, UsersIntent, UsersAction>.currentState(): UsersState {
        var s = UsersState()
        withState { s = this }
        return s
    }

    private suspend fun PipelineContext<UsersState, UsersIntent, UsersAction>.reload() {
        val s = currentState()
        val statusParam = if (s.statusFilter == "All") null else s.statusFilter
        updateState { copy(data = AsyncSection.Loading) }
        val resp = repo.getUsers(page = s.page, pageSize = 20, search = s.search.ifBlank { null }, status = statusParam, sortBy = s.sortBy, sortAsc = s.sortAsc)
        updateState {
            copy(
                data = AsyncSection.Loaded(resp.data),
                totalPages = resp.meta?.totalPages ?: 1,
                totalCount = resp.meta?.totalCount ?: 0L,
            )
        }
    }
}
